import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { GenericFormPage } from '../../components/generic';
import {
  useSupportTicket,
  SupportTicket,
  useSupportAgreementsForSelect,
  useTasksForSelect,
  useTicketStatusesForSelect,
} from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const { data: ticketData } = useSupportTicket(isEdit ? parseInt(id!) : 0);

  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(
    ticketData?.clientId
  );

  useEffect(() => {
    if (ticketData?.clientId) {
      setSelectedClientId(ticketData.clientId);
    } else if (!isEdit) {
      setSelectedClientId(undefined);
    }
  }, [ticketData?.clientId, isEdit]);

  const { options: agreementOptions } = useSupportAgreementsForSelect(selectedClientId);
  const { options: taskOptions } = useTasksForSelect(selectedClientId);
  const { options: statusOptions } = useTicketStatusesForSelect();

  const configWithOptions = useMemo(() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client',
          options: [],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'assignedToId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Assigned To',
          placeholder: 'Select a user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'statusId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Status',
          placeholder: 'Select status',
          options: statusOptions,
        };
      }
      if (field.name === 'priority') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Priority',
          placeholder: 'Select priority (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-priority',
          labelField: 'p_descr',
          valueField: 'id' as const,
        };
      }
      // Support Agreement select (filtered by client)
      if (field.name === 'supportAgreementId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Support Agreement',
          placeholder: selectedClientId ? 'Select a support agreement (optional)' : 'Select a client first',
          options: [
            { value: '', label: 'None' },
            ...agreementOptions,
          ],
          disabled: !selectedClientId,
          helperText: !selectedClientId ? 'Please select a client first to see available agreements' : undefined,
        };
      }
      // Task select (filtered by client)
      if (field.name === 'taskId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Task',
          placeholder: selectedClientId ? 'Select a task (optional)' : 'Select a client first',
          options: [
            { value: '', label: 'None' },
            ...taskOptions,
          ],
          disabled: !selectedClientId,
          helperText: !selectedClientId ? 'Please select a client first to see available tasks' : undefined,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormSupportTicket = Omit<SupportTicket, 'clientId' | 'assignedToId' | 'statusId' | 'priority' | 'supportAgreementId' | 'taskId'> & {
      clientId?: string | number;
      assignedToId?: string | number;
      statusId?: string | number;
      priority?: string | number;
      supportAgreementId?: string | number;
      taskId?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: SupportTicket): Partial<FormSupportTicket> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        assignedToId: baseData.assignedToId ? String(baseData.assignedToId) : '',
        statusId: baseData.statusId ? String(baseData.statusId) : '',
        priority: baseData.priority ? String(baseData.priority) : '',
        supportAgreementId: baseData.supportAgreementId ? String(baseData.supportAgreementId) : '',
        taskId: baseData.taskId ? String(baseData.taskId) : '',
      };
    };

    // Custom getInitialFormData
    const customGetInitialFormData = (): Partial<FormSupportTicket> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        assignedToId: '',
        statusId: '',
        priority: '',
        supportAgreementId: '',
        taskId: '',
      };
    };

    // Helper function to convert string to number or undefined
    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') {
        return undefined;
      }
      return Number(value);
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormSupportTicket>, files?: File[], filesToDetach?: number[]) => {
      const processedData: Partial<SupportTicket> = {
        ...data,
        clientId: convertToNumber(data.clientId),
        assignedToId: convertToNumber(data.assignedToId),
        statusId: convertToNumber(data.statusId),
        priority: convertToNumber(data.priority),
        supportAgreementId: convertToNumber(data.supportAgreementId),
        taskId: convertToNumber(data.taskId),
      };
      return await moduleConfig.form.create(processedData, files, filesToDetach);
    };

    const customUpdate = async (id: number, data: Partial<FormSupportTicket>, files?: File[], filesToDetach?: number[]) => {
      const processedData: Partial<SupportTicket> = {
        ...data,
        clientId: convertToNumber(data.clientId),
        assignedToId: convertToNumber(data.assignedToId),
        statusId: convertToNumber(data.statusId),
        priority: convertToNumber(data.priority),
        supportAgreementId: convertToNumber(data.supportAgreementId),
        taskId: convertToNumber(data.taskId),
      };
      return await moduleConfig.form.update(id, processedData, files, filesToDetach);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<SupportTicket>,
      mapItemToFormData: customMapItemToFormData as (item: SupportTicket) => Partial<SupportTicket>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<SupportTicket>, files?: File[], filesToDetach?: number[]) => Promise<SupportTicket>,
        update: customUpdate as (id: number, data: Partial<SupportTicket>, files?: File[], filesToDetach?: number[]) => Promise<SupportTicket>,
      },
    };
  }, [agreementOptions, taskOptions, selectedClientId, statusOptions]);

  // Note: For new forms, the user will need to save and edit again to see filtered agreements/tasks
  // This is a limitation of the current GenericFormPage architecture
  // For edit mode, the clientId is already set from the ticket data

  return (
    <GenericFormPage<SupportTicket>
      useData={useSupportTicket}
      config={configWithOptions}
    />
  );
};

export default Form;
